"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useCallback, useMemo } from "react";
import FormSection from "../ui/FormSection";
import ValidationError from "../ui/ValidationError";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
interface ArtworkDetailsProps {
  readonly title: string;
  readonly description: string;
  readonly onTitleChange: (title: string) => void;
  readonly onDescriptionChange: (description: string) => void;
  readonly titleError?: string | null;
  readonly descriptionError?: string | null;
  readonly onTitleBlur?: () => void;
  readonly onDescriptionBlur?: () => void;
}

/**
 * ArtworkDetails - Component for the artwork title and description fields
 *
 * Extreme simplification using uncontrolled inputs with refs for maximum performance
 */
const ArtworkDetails: React.FC<ArtworkDetailsProps> = t0 => {
  const $ = _c(65);
  const {
    title,
    description,
    onTitleChange,
    onDescriptionChange,
    titleError,
    descriptionError,
    onTitleBlur,
    onDescriptionBlur
  } = t0;
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== title) {
    t1 = () => {
      if (titleRef.current && title && titleRef.current.value !== title) {
        titleRef.current.value = title;
      }
    };
    t2 = [title];
    $[0] = title;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  React.useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== description) {
    t3 = () => {
      if (descriptionRef.current && description && descriptionRef.current.value !== description) {
        descriptionRef.current.value = description;
      }
    };
    t4 = [description];
    $[3] = description;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[6] !== onTitleBlur || $[7] !== onTitleChange || $[8] !== title) {
    t5 = () => {
      if (titleRef.current && titleRef.current.value !== title) {
        onTitleChange(titleRef.current.value);
      }
      if (onTitleBlur) {
        onTitleBlur();
      }
    };
    $[6] = onTitleBlur;
    $[7] = onTitleChange;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleTitleBlur = t5;
  let t6;
  if ($[10] !== description || $[11] !== onDescriptionBlur || $[12] !== onDescriptionChange) {
    t6 = () => {
      if (descriptionRef.current && descriptionRef.current.value !== description) {
        onDescriptionChange(descriptionRef.current.value);
      }
      if (onDescriptionBlur) {
        onDescriptionBlur();
      }
    };
    $[10] = description;
    $[11] = onDescriptionBlur;
    $[12] = onDescriptionChange;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleDescriptionBlur = t6;
  let t7;
  if ($[14] !== title) {
    t7 = title.trim();
    $[14] = title;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const isTitleFilled = t7.length > 0;
  let t8;
  if ($[16] !== description) {
    t8 = description.trim();
    $[16] = description;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const isDescriptionFilled = t8.length > 0;
  const t9 = `tw-absolute tw-left-3 -tw-top-2 tw-px-1 tw-text-xs tw-font-medium tw-bg-iron-900 tw-z-10 tw-transition-all
                ${titleError ? "tw-text-red" : "tw-text-iron-300 group-focus-visible-within:tw-text-primary-400"}`;
  let t10;
  if ($[18] !== t9) {
    t10 = <label htmlFor="field-title" className={t9}>Artwork Title</label>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = title || "";
  const t12 = !!titleError;
  const t13 = titleError ? "title-error" : undefined;
  const t14 = `tw-form-input tw-w-full tw-rounded-lg tw-px-4 tw-py-3.5 tw-text-sm tw-text-iron-100 tw-transition-all tw-duration-500 tw-ease-in-out
                  tw-bg-iron-900 tw-border-0 tw-outline-none placeholder:tw-text-iron-500 tw-cursor-text tw-ring-1
                  ${titleError ? "tw-ring-red" : "tw-ring-iron-700 desktop-hover:hover:tw-ring-iron-650 focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 focus-visible:hover:tw-ring-primary-400"}
                  ${isTitleFilled && !titleError ? "tw-pr-10" : ""}
                  `;
  let t15;
  if ($[20] !== handleTitleBlur || $[21] !== t11 || $[22] !== t12 || $[23] !== t13 || $[24] !== t14) {
    t15 = <input ref={titleRef} id="field-title" name="title" type="text" maxLength={500} defaultValue={t11} onBlur={handleTitleBlur} aria-invalid={t12} aria-describedby={t13} data-field="title" className={t14} />;
    $[20] = handleTitleBlur;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isTitleFilled || $[27] !== titleError) {
    t16 = isTitleFilled && !titleError && <div className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-pointer-events-none"><CheckCircleIcon className="tw-text-emerald-500 tw-w-5 tw-h-5 tw-flex-shrink-0" /></div>;
    $[26] = isTitleFilled;
    $[27] = titleError;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <div className="tw-relative tw-rounded-xl tw-bg-iron-950 tw-transition-all tw-duration-200">{t15}{t16}</div>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== t10 || $[33] !== t17) {
    t18 = <div className="tw-relative">{t10}{t17}</div>;
    $[32] = t10;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== titleError) {
    t19 = <ValidationError error={titleError} id="title-error" />;
    $[35] = titleError;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t18 || $[38] !== t19) {
    t20 = <div className="tw-group tw-relative">{t18}{t19}</div>;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = `tw-absolute tw-left-3 -tw-top-2 tw-px-1 tw-text-xs tw-font-medium tw-bg-iron-900 tw-z-10 tw-transition-all
                ${descriptionError ? "tw-text-red" : "tw-text-iron-300 group-focus-visible-within:tw-text-primary-400"}`;
  let t22;
  if ($[40] !== t21) {
    t22 = <label htmlFor="field-description" className={t21}>Description</label>;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = description || "";
  const t24 = !!descriptionError;
  const t25 = descriptionError ? "description-error" : undefined;
  const t26 = `tw-form-textarea tw-w-full tw-rounded-lg tw-px-4 tw-py-3.5 tw-text-sm tw-text-iron-100 tw-transition-all tw-duration-500 tw-ease-in-out
                  tw-bg-iron-900 tw-border-0 tw-outline-none placeholder:tw-text-iron-500 tw-resize-none tw-cursor-text tw-ring-1
                  ${descriptionError ? "tw-ring-red" : "tw-ring-iron-700 desktop-hover:hover:tw-ring-iron-650 focus-visible:tw-ring-2 focus-visible:tw-ring-primary-400 focus-visible:hover:tw-ring-primary-400"}
                  ${isDescriptionFilled && !descriptionError ? "tw-pr-10" : ""}
                  `;
  let t27;
  if ($[42] !== handleDescriptionBlur || $[43] !== t23 || $[44] !== t24 || $[45] !== t25 || $[46] !== t26) {
    t27 = <textarea ref={descriptionRef} id="field-description" name="description" defaultValue={t23} onBlur={handleDescriptionBlur} rows={4} maxLength={500} aria-invalid={t24} aria-describedby={t25} data-field="description" className={t26} />;
    $[42] = handleDescriptionBlur;
    $[43] = t23;
    $[44] = t24;
    $[45] = t25;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== descriptionError || $[49] !== isDescriptionFilled) {
    t28 = isDescriptionFilled && !descriptionError && <div className="tw-absolute tw-right-3 tw-top-3 tw-pointer-events-none"><CheckCircleIcon className="tw-text-emerald-500 tw-w-5 tw-h-5 tw-flex-shrink-0" /></div>;
    $[48] = descriptionError;
    $[49] = isDescriptionFilled;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== t27 || $[52] !== t28) {
    t29 = <div className="tw-relative tw-rounded-xl tw-bg-iron-950 tw-transition-all tw-duration-200">{t27}{t28}</div>;
    $[51] = t27;
    $[52] = t28;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== t22 || $[55] !== t29) {
    t30 = <div className="tw-relative">{t22}{t29}</div>;
    $[54] = t22;
    $[55] = t29;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== descriptionError) {
    t31 = <ValidationError error={descriptionError} id="description-error" />;
    $[57] = descriptionError;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== t30 || $[60] !== t31) {
    t32 = <div className="tw-group tw-relative">{t30}{t31}</div>;
    $[59] = t30;
    $[60] = t31;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] !== t20 || $[63] !== t32) {
    t33 = <FormSection title="Artwork Details" titleClassName="tw-text-lg sm:tw-text-xl tw-font-semibold tw-text-iron-100 tw-mb-4 sm:tw-mb-6"><div className="tw-grid tw-grid-cols-1 tw-gap-6">{t20}{t32}</div></FormSection>;
    $[62] = t20;
    $[63] = t32;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  return t33;
};

// Use memo to prevent unnecessary re-renders
export default React.memo(ArtworkDetails);